import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { clone as skeletonClone } from 'three/examples/jsm/utils/SkeletonUtils.js';
import type { AntCaste, AntSimWorld, AntRole, AntState, AntTask } from '../sim/contracts';
import { ANT_VISUAL_LENGTH } from '../sim/contracts';
import type { Terrain } from '../game/Terrain';
import { readTune } from '../ui/TuneStore';

/** Visual variants derived from (role, caste). Queens and non-worker castes
 *  are their own variants; worker falls through to the default 'normal'. */
type AppearanceKey = AntRole | 'queen' | 'soldier' | 'nurse' | 'scout' | 'breeder';

/** Per-caste body tint used when a normal-role ant's caste overrides the
 *  default 'normal' variant. Worker uses the plain 'normal' variant. */
interface CasteTint { color: number; emissive: number; translucent?: boolean }
const CASTE_TINTS: Record<'soldier' | 'nurse' | 'scout' | 'breeder', CasteTint> = {
  // Soldier: darkest red. Worker is the plain red elsewhere in the pipeline.
  soldier: { color: 0x3a0606, emissive: 0x120000 },
  nurse:   { color: 0xe8d8b0, emissive: 0x2a2418 },
  // Scout: orange-red, slightly see-through so they read as faster / lighter.
  scout:   { color: 0xe05018, emissive: 0x3a1006, translucent: true },
  breeder: { color: 0x6b2a5a, emissive: 0x1a0a18 },
};

/** Lerp a packed 0xRRGGBB color toward black by `t` in [0,1]. t=0 returns the
 *  input unchanged; t=1 returns 0x000000. Used to darken team-1 caste tints. */
function lerpToBlack(hex: number, t: number): number {
  if (t <= 0) return hex;
  const r = (hex >> 16) & 0xff;
  const g = (hex >> 8) & 0xff;
  const b = hex & 0xff;
  const k = 1 - t;
  const nr = Math.round(r * k);
  const ng = Math.round(g * k);
  const nb = Math.round(b * k);
  return (nr << 16) | (ng << 8) | nb;
}

import { assetUrl as _assetUrl } from '../ui/AssetMap';
const MODEL_SLOT = 'worker-ant';
const MODEL_URL_DEFAULT = '/assets/worker-ant.glb';
const SPEED_WALK_THRESHOLD = 0.01; // m/s above which we play Walk, else Idle
const LOD_ANIMATE_DISTANCE = 18;   // metres — past here we freeze the mixer
const LOD_ANIMATE_DISTANCE_SQ = LOD_ANIMATE_DISTANCE * LOD_ANIMATE_DISTANCE;
/** Past this distance the ant isn't rendered at all — the fog hides the
 *  far colony anyway, and culling here saves the per-mesh draw cost. */
// Hard cull inside the fog fade (FogExp2 density ≈ 0.035 → ~85 % opacity at
// 22 m). Anything beyond this is effectively invisible behind fog anyway, so
// skip rendering + skeleton updates entirely.
const LOD_CULL_DISTANCE = 22;
const LOD_CULL_DISTANCE_SQ = LOD_CULL_DISTANCE * LOD_CULL_DISTANCE;

// Logical action keys we use. We map these to GLB clip names tolerantly via
// regex so slight naming variations don't brick the avatar.
type ActionKey =
  | 'walk' | 'idle1' | 'idle2' | 'idle3' | 'idle4'
  | 'eat' | 'feed' | 'dig' | 'build' | 'jump' | 'drink'
  | 'bite' | 'attack1' | 'attack2' | 'grab' | 'stingOther' | 'stingHuman' | 'formicAcid' | 'fly';

const CLIP_PATTERNS: Record<ActionKey, RegExp[]> = {
  walk: [/^walk$/i, /walk/i],
  idle1: [/^idle$/i],
  idle2: [/^idle$/i, /idle\.001/i],
  idle3: [/idle\.002/i],
  idle4: [/^Idle$/],
  eat: [/^eat$/i],
  feed: [/Trophalaxis\(Feed Larvae\)/i, /Trophalaxis/i],
  dig: [/^dig$/i],
  build: [/^build$/i],
  jump: [/^jump$/i],
  drink: [/^drink$/i],
  bite: [/^bite$/i],
  // Tolerant matching for the worker-ant GLB's "Attack 1" / "Attack 2"
  // clips — spaces, underscores, dots, or tight-packed all work.
  attack1: [/^attack[\s._-]*1$/i, /attack[\s._-]*1/i],
  attack2: [/^attack[\s._-]*2$/i, /attack[\s._-]*2/i],
  grab: [/grab\/heavy bite/i, /grab/i],
  stingOther: [/Sting\(other ant\)/i],
  stingHuman: [/Sting\(human\)/i],
  formicAcid: [/Formic Acid/i],
  fly: [/Fly\(Sprint\)/i, /Fly/i],
};

interface Avatar {
  root: THREE.Group;
  mixer: THREE.AnimationMixer;
  actions: Partial<Record<ActionKey, THREE.AnimationAction>>;
  clipDurations: Partial<Record<ActionKey, number>>;
  activeKey: ActionKey;
  oneShotUntil: number;    // elapsed seconds; 0 if not in a one-shot
  prevTask: AntTask;
  prevCarrying: boolean;
  prevGiftFlashT: number;  // previous-frame giftFlashT; rising edge triggers eat
  nextFlavorRoll: number;
  currentAppearance: string | null;
  meshes: THREE.Mesh[];
  meshTplIdx: number[][];
  /** Little sphere in the ant's mandibles, visible only when carrying. */
  carryMesh: THREE.Mesh;
}

/**
 * Animated colony — every ant is an independent cloned SkinnedMesh driven by
 * its own AnimationMixer. State machine per ant:
 *   moving → walk
 *   stopped → random idle variant
 *   just picked up food → eat (one-shot)
 *   just dropped at nest → Trophalaxis/Feed Larvae (one-shot)
 *   occasional flavor: dig, build, jump, drink near nest / water
 */
export class AntColony {
  readonly group = new THREE.Group();
  private avatars: Avatar[] = [];
  private matByTeam!: [Record<AppearanceKey, THREE.Material[]>, Record<AppearanceKey, THREE.Material[]>];

  // State captured during load() and reused by lazy createAvatar(). Keeping
  // these around lets us defer the per-avatar SkeletonUtils.clone +
  // AnimationMixer setup until an ant index is actually populated, instead
  // of pre-allocating capacity (≈1200) clones at boot. Without lazy alloc
  // the scene graph holds ~150k Object3D nodes which torpedoes frame
  // matrix-update cost.
  private srcScene: THREE.Object3D | null = null;
  private resolvedClips: Partial<Record<ActionKey, THREE.AnimationClip>> = {};
  private nameToIdx: Map<string, number> = new Map();
  private carryGeo: THREE.SphereGeometry | null = null;
  private carryMat: THREE.MeshStandardMaterial | null = null;

  private readonly forward = new THREE.Vector3();
  private readonly up = new THREE.Vector3();

  constructor(
    private world: AntSimWorld,
    private terrain: Terrain,
    private capacity: number,
  ) {
    this.group.name = 'ant-colony';
  }

  async load(): Promise<void> {
    const gltf = await new GLTFLoader().loadAsync(_assetUrl(MODEL_SLOT, MODEL_URL_DEFAULT));
    const srcScene = gltf.scene;
    const clips = gltf.animations;

    // Pull tuned scale/rotation/offset from the /insects tuner shared store.
    // Default keeps the previous auto-size behaviour if nothing is stored.
    const tune = readTune('worker-ant', { scale: 0, rotX: 0, rotY: Math.PI / 2, rotZ: 0, offY: 0 });

    if (tune.scale > 0) {
      srcScene.rotation.set(tune.rotX, tune.rotY, tune.rotZ);
      srcScene.scale.setScalar(tune.scale);
      srcScene.updateMatrixWorld(true);
      const sb = new THREE.Box3().setFromObject(srcScene);
      const c = new THREE.Vector3();
      sb.getCenter(c);
      srcScene.position.set(-c.x, -sb.min.y + tune.offY, -c.z);
      srcScene.updateMatrixWorld(true);
    } else {
      srcScene.rotation.y = Math.PI / 2;
      srcScene.updateMatrixWorld(true);
      const bb = new THREE.Box3().setFromObject(srcScene);
      const size = new THREE.Vector3();
      bb.getSize(size);
      const longest = Math.max(size.x, size.y, size.z) || 1;
      srcScene.scale.setScalar(ANT_VISUAL_LENGTH / longest);
      srcScene.updateMatrixWorld(true);
      const bb2 = new THREE.Box3().setFromObject(srcScene);
      const center = new THREE.Vector3();
      bb2.getCenter(center);
      srcScene.position.set(-center.x, -bb2.min.y, -center.z);
      srcScene.updateMatrixWorld(true);
    }

    // Resolve each ActionKey to a clip from the GLB (tolerant regex).
    const resolved: Partial<Record<ActionKey, THREE.AnimationClip>> = {};
    for (const key of Object.keys(CLIP_PATTERNS) as ActionKey[]) {
      for (const pat of CLIP_PATTERNS[key]) {
        const c = clips.find((cl) => pat.test(cl.name));
        if (c) { resolved[key] = c; break; }
      }
    }

    // Template materials — keyed by name so cloned meshes can always find
    // their original material identity regardless of reference sharing.
    const byName = new Map<string, THREE.Material>();
    srcScene.traverse((o) => {
      const m = o as THREE.Mesh;
      if (!m.isMesh) return;
      const mats = Array.isArray(m.material) ? m.material : [m.material];
      for (const mat of mats) {
        if (mat && mat.name && !byName.has(mat.name)) byName.set(mat.name, mat);
      }
    });
    const templateMats = Array.from(byName.values());
    const nameToIdx = new Map(templateMats.map((m, i) => [m.name, i]));

    // "Body-like" variants: anything whose name contains 'material' (the red
    // chitin) OR lacks a baseColorFactor override (skelet uses the default
    // white diffuse — we want to tint that too or it reads as pure white).
    // We tint all materials for player/follower roles so the ant reads as a
    // solid color silhouette at distance.
    const tintBodyIdx = new Set<number>();
    for (let i = 0; i < templateMats.length; i++) {
      const n = (templateMats[i].name || '').toLowerCase();
      // Leave eyes black — tinting them blows out the highlights.
      if (!n.includes('eye')) tintBodyIdx.add(i);
    }
    // Build one team's full variant record. `tint01` applies a per-channel
    // lerp toward black (0..1) so team 1 (black) silhouettes still read as
    // "black team" even under the per-caste tints.
    const buildTeamMats = (blacken: number): Record<AppearanceKey, THREE.Material[]> => {
      const casteVariant = (color: number, emissive: number, translucent = false) =>
        templateMats.map((m, i) =>
          tintBodyIdx.has(i)
            ? this.tint(
                m.clone() as THREE.MeshStandardMaterial,
                lerpToBlack(color, blacken),
                lerpToBlack(emissive, blacken),
                translucent,
              )
            : m.clone(),
        );

      const normal: THREE.Material[] = blacken > 0
        ? templateMats.map((m, i) =>
            tintBodyIdx.has(i)
              ? this.tint(m.clone() as THREE.MeshStandardMaterial, 0x1a1a1f, 0x0a0a12)
              : m.clone(),
          )
        : templateMats.map((m) => m.clone());

      // Skelet-darken pass: the default-white skelet material reads as pure
      // white if untouched; give it a sensible brown for red, cool dark for black.
      for (let i = 0; i < normal.length; i++) {
        const src = templateMats[i] as THREE.MeshStandardMaterial;
        const needsDarken = !src.name?.toLowerCase().includes('material') && !src.name?.toLowerCase().includes('eye');
        if (needsDarken) {
          const mm = normal[i] as THREE.MeshStandardMaterial;
          mm.color = new THREE.Color(blacken > 0 ? 0x0e0e12 : 0xc23025);
        }
      }

      return {
        normal,
        player: templateMats.map((m, i) =>
          tintBodyIdx.has(i)
            ? this.tint(m.clone() as THREE.MeshStandardMaterial, 0xffd23a, 0x5a3a00)
            : m.clone(),
        ),
        follower: templateMats.map((m, i) =>
          tintBodyIdx.has(i)
            ? this.tint(m.clone() as THREE.MeshStandardMaterial, 0x4dd2ff, 0x0a2a3a)
            : m.clone(),
        ),
        queen: templateMats.map((m, i) =>
          tintBodyIdx.has(i)
            ? this.tint(m.clone() as THREE.MeshStandardMaterial, 0xd4a017, 0x3a2a00)
            : m.clone(),
        ),
        soldier: casteVariant(CASTE_TINTS.soldier.color, CASTE_TINTS.soldier.emissive, CASTE_TINTS.soldier.translucent),
        nurse:   casteVariant(CASTE_TINTS.nurse.color,   CASTE_TINTS.nurse.emissive,   CASTE_TINTS.nurse.translucent),
        scout:   casteVariant(CASTE_TINTS.scout.color,   CASTE_TINTS.scout.emissive,   CASTE_TINTS.scout.translucent),
        breeder: casteVariant(CASTE_TINTS.breeder.color, CASTE_TINTS.breeder.emissive, CASTE_TINTS.breeder.translucent),
      };
    };

    const redMats = buildTeamMats(0);
    const blackMats = buildTeamMats(0.4); // 40% lerp toward black

    this.matByTeam = [redMats, blackMats];

    // Shared "carried food" sphere — one mesh per ant, toggled visible
    // while transporting. Geometry + material are shared across all ants.
    this.carryGeo = new THREE.SphereGeometry(ANT_VISUAL_LENGTH * 0.09, 8, 6);
    this.carryMat = new THREE.MeshStandardMaterial({ color: 0xffffff, roughness: 0.4 });

    // Stash the deps for lazy createAvatar(). No pre-allocation here —
    // avatars are constructed on demand in update() the first time an
    // ant index is touched. Capacity is now a soft ceiling, not a
    // pre-allocation count.
    this.srcScene = srcScene;
    this.resolvedClips = resolved;
    this.nameToIdx = nameToIdx;
  }

  private createAvatar(idx: number): Avatar | null {
    if (!this.srcScene || !this.carryGeo || !this.carryMat) return null;
    const root = new THREE.Group();
    root.name = `ant-${idx}`;
    root.matrixAutoUpdate = false;

    const carry = new THREE.Mesh(this.carryGeo, this.carryMat);
    carry.position.set(ANT_VISUAL_LENGTH * 0.55, ANT_VISUAL_LENGTH * 0.18, 0);
    carry.castShadow = true;
    carry.visible = false;
    root.add(carry);

    const cloned = skeletonClone(this.srcScene);
    root.add(cloned);

    const meshes: THREE.Mesh[] = [];
    const meshTplIdx: number[][] = [];
    cloned.traverse((o) => {
      const m = o as THREE.Mesh;
      if (!m.isMesh) return;
      meshes.push(m);
      const refs = Array.isArray(m.material) ? m.material : [m.material];
      meshTplIdx.push(refs.map((r) => this.nameToIdx.get(r?.name ?? '') ?? 0));
      m.castShadow = true;
      m.receiveShadow = true;
    });

    const mixer = new THREE.AnimationMixer(cloned);
    const actions: Partial<Record<ActionKey, THREE.AnimationAction>> = {};
    const clipDurations: Partial<Record<ActionKey, number>> = {};
    for (const key of Object.keys(this.resolvedClips) as ActionKey[]) {
      const clip = this.resolvedClips[key];
      if (!clip) continue;
      const act = mixer.clipAction(clip);
      actions[key] = act;
      clipDurations[key] = clip.duration;
    }
    const walk = actions.walk;
    if (walk) {
      walk.play();
      walk.setEffectiveWeight(1);
      walk.time = Math.random() * (clipDurations.walk ?? 1);
    }

    const av: Avatar = {
      root,
      mixer,
      actions,
      clipDurations,
      activeKey: 'walk',
      oneShotUntil: 0,
      prevTask: 'search',
      prevCarrying: false,
      prevGiftFlashT: 0,
      nextFlavorRoll: 4 + Math.random() * 6,
      currentAppearance: null,
      meshes,
      meshTplIdx,
      carryMesh: carry,
    };
    this.avatars[idx] = av;
    this.group.add(root);
    return av;
  }

  syncInstances(): void { /* unused — animation pipeline runs in update() */ }

  update(dt: number, elapsed: number, camera?: THREE.Camera, playerUnderground = false): void {
    const ants = this.world.ants;
    const count = Math.min(ants.length, this.capacity);
    const cx = camera?.position.x ?? 0;
    const cy = camera?.position.y ?? 0;
    const cz = camera?.position.z ?? 0;

    for (let i = 0; i < count; i++) {
      const a = ants[i];
      // Lazy-allocate. Avatar creation is expensive (skeleton clone +
      // AnimationMixer), so we defer it to the first frame each index
      // is touched.
      let av = this.avatars[i];
      if (!av) {
        if (i >= this.capacity) continue;
        av = this.createAvatar(i) ?? undefined!;
        if (!av) continue;
      }

      if (!a.alive) {
        // Corpse — tipped on its side, sinking, hidden after ~30s.
        if (a.deathT > 30) {
          av.root.visible = false;
          continue;
        }
        av.root.visible = true;
        this.applyAppearance(av, a.team, a.role, a.casteType);
        this.writeDeadPose(av.root, a);
        continue;
      }

      // Cull ants that are underground (nestCycleT > 0) while the yellow
      // ant is on the surface — the player can't see them anyway, so skip
      // render + skeleton update entirely. If the player IS underground,
      // the surface colony is already hidden via NestMode's surfaceRoots.
      if (a.nestCycleT > 0 && !playerUnderground && a.role !== 'player') {
        av.root.visible = false;
        continue;
      }

      // Compute distance to camera first — we use it for BOTH animate-LOD
      // and outright render culling.
      const dSq = camera
        ? (a.x - cx) * (a.x - cx) + (a.y - cy) * (a.y - cy) + (a.z - cz) * (a.z - cz)
        : 0;
      if (camera && dSq > LOD_CULL_DISTANCE_SQ && a.role !== 'player') {
        av.root.visible = false;
        continue;
      }
      this.writePose(av.root, a);
      this.applyAppearance(av, a.team, a.role, a.casteType);
      if (av.carryMesh.visible !== a.carrying) av.carryMesh.visible = a.carrying;

      // LOD — skip the expensive mixer update for ants far from the camera.
      // Position still updates (so they move on the minimap and between frames
      // when the camera returns), but their skeleton freezes at its current pose.
      const fightingFlag = a.attacking;
      const animate = !camera || dSq < LOD_ANIMATE_DISTANCE_SQ || a.role === 'player' || fightingFlag;

      const inOneShot = av.oneShotUntil > 0 && elapsed < av.oneShotUntil;
      if (av.oneShotUntil > 0 && !inOneShot) av.oneShotUntil = 0;

      const fighting = a.attacking;

      if (a.role === 'normal' && !fighting) {
        // Rising edge of carrying = worker just picked up food → bite.
        // Falling edge at the nest = food delivery → trophalaxis.
        if (!av.prevCarrying && a.carrying) this.playOneShot(av, 'bite', elapsed);
        else if (av.prevCarrying && !a.carrying) this.playOneShot(av, 'feed', elapsed);
      }
      // Trophalaxis: rising edge of giftFlashT triggers 'feed' on BOTH
      // donor and recipient (the sim sets giftFlashT + giftPartnerId on
      // each side simultaneously).
      if (!fighting && av.prevGiftFlashT <= 0 && a.giftFlashT > 0) {
        this.playOneShot(av, 'feed', elapsed);
      }
      av.prevGiftFlashT = a.giftFlashT;
      av.prevCarrying = a.carrying;
      av.prevTask = a.task;

      if (fighting) {
        const fightKey = this.pickFightKey(av, elapsed);
        if (fightKey && av.activeKey !== fightKey) this.switchTo(av, fightKey, 0.12);
      } else {
        if (animate && !inOneShot && elapsed > av.nextFlavorRoll) {
          av.nextFlavorRoll = elapsed + 6 + Math.random() * 10;
          const r = Math.random();
          const nearNest = Math.hypot(a.x, a.z) < 3.5;
          if (a.speed < SPEED_WALK_THRESHOLD) {
            if (nearNest && r < 0.5) this.playOneShot(av, r < 0.25 ? 'dig' : 'build', elapsed);
            else if (r < 0.1) this.playOneShot(av, 'jump', elapsed);
            else if (r < 0.3) this.playOneShot(av, this.pickIdle(av), elapsed, 2.5);
          } else if (r < 0.03) {
            this.playOneShot(av, 'jump', elapsed);
          }
        }

        if (animate && !inOneShot) {
          let target: ActionKey;
          if (a.speed > SPEED_WALK_THRESHOLD) {
            target = 'walk';
          } else if (a.casteType === 'soldier' && a.role !== 'follower') {
            // Soldiers at their post cycle through idle → idle.001 → idle.002.
            target = this.pickSoldierIdle(av, elapsed);
          } else {
            target = this.pickIdle(av);
          }
          if (av.activeKey !== target) {
            // Going FROM walk TO idle must snap (0 s) — when an ant halts the
            // legs must stop the same frame, not crossfade. Idle → walk stays
            // smooth so starts don't pop.
            const dur = av.activeKey === 'walk' && target !== 'walk' ? 0 : 0.25;
            this.switchTo(av, target, dur);
          }
          const walk = av.actions.walk;
          if (walk && target === 'walk') {
            // Scale legs with actual body speed so the cycle matches the
            // 1.5×-faster baseline. At 0.9 m/s max this lands ~6 × tempo.
            walk.timeScale = 0.9 + 6.0 * Math.min(1, a.speed / 0.8);
          }
        }
      }

      if (animate) av.mixer.update(dt);
      av.root.visible = true;
    }
    for (let i = count; i < this.avatars.length; i++) this.avatars[i].root.visible = false;
  }

  /** Alternate between Attack 1 and Attack 2 on a ~0.9 s cadence so a
   *  sustained fight reads as active swings, not one held pose. Per-ant
   *  offset (name-hashed) staggers the swing so a cluster of fighters
   *  doesn't strike in lockstep. Falls back to bite/sting/grab when the
   *  GLB has no attack clips. */
  private pickFightKey(av: Avatar, elapsed: number): ActionKey | null {
    const hasA1 = !!av.actions.attack1;
    const hasA2 = !!av.actions.attack2;
    if (hasA1 && hasA2) {
      const name = av.root.name;
      let h = 0;
      for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) | 0;
      const offset = (Math.abs(h) % 100) / 100;  // 0..1
      const bucket = Math.floor((elapsed + offset) / 0.9) % 2;
      return bucket === 0 ? 'attack1' : 'attack2';
    }
    if (hasA1) return 'attack1';
    if (hasA2) return 'attack2';
    const fallback: ActionKey[] = ['bite', 'stingOther', 'grab'];
    for (const k of fallback) if (av.actions[k]) return k;
    return null;
  }

  private pickIdle(av: Avatar): ActionKey {
    const candidates: ActionKey[] = ['idle1', 'idle2', 'idle3', 'idle4'];
    // Pick deterministically based on ant id via mixer root name to avoid
    // flicker, but vary across ants.
    const name = av.root.name;
    let h = 0;
    for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) | 0;
    for (let off = 0; off < candidates.length; off++) {
      const k = candidates[(Math.abs(h) + off) % candidates.length];
      if (av.actions[k]) return k;
    }
    return 'walk';
  }

  /** Soldier idles cycle every ~5 s between idle/idle.001/idle.002 so a rim
   *  of sentries never looks frozen. Hash of the root name staggers each
   *  soldier's cycle so they don't all switch together. */
  private pickSoldierIdle(av: Avatar, elapsed: number): ActionKey {
    const candidates: ActionKey[] = ['idle1', 'idle2', 'idle3'];
    const name = av.root.name;
    let h = 0;
    for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) | 0;
    const bucket = (Math.floor(elapsed / 5) + Math.abs(h)) % candidates.length;
    for (let off = 0; off < candidates.length; off++) {
      const k = candidates[(bucket + off) % candidates.length];
      if (av.actions[k]) return k;
    }
    return 'walk';
  }

  private switchTo(av: Avatar, next: ActionKey, duration: number): void {
    if (av.activeKey === next) return;
    const from = av.actions[av.activeKey];
    const to = av.actions[next];
    if (!to) return;
    to.reset();
    to.setLoop(THREE.LoopRepeat, Infinity);
    to.clampWhenFinished = false;
    to.enabled = true;
    to.play();
    if (from && from !== to) {
      from.crossFadeTo(to, duration, false);
    } else {
      to.setEffectiveWeight(1);
    }
    av.activeKey = next;
  }

  private playOneShot(av: Avatar, key: ActionKey, elapsed: number, durationOverride?: number): void {
    const to = av.actions[key];
    if (!to) return;
    const dur = durationOverride ?? av.clipDurations[key] ?? 1;
    const from = av.actions[av.activeKey];
    to.reset();
    to.setLoop(THREE.LoopOnce, 1);
    to.clampWhenFinished = true;
    to.enabled = true;
    to.play();
    if (from && from !== to) from.crossFadeTo(to, 0.12, false);
    else to.setEffectiveWeight(1);
    av.activeKey = key;
    av.oneShotUntil = elapsed + dur + 0.05;
  }

  private applyAppearance(av: Avatar, team: 0 | 1, role: AntRole, caste: AntCaste): void {
    // Priority: queen > player/follower role override > caste tint > plain normal.
    // Queens always wear the gold variant regardless of role — losing the role
    // tint for the queen is an intentional trade for easier queen spotting.
    let variant: AppearanceKey;
    if (caste === 'queen') variant = 'queen';
    else if (role === 'player' || role === 'follower') variant = role;
    else if (caste === 'soldier' || caste === 'nurse' || caste === 'scout' || caste === 'breeder') variant = caste;
    else variant = 'normal'; // worker + normal role
    const key = `${team}-${variant}`;
    if (av.currentAppearance === key) return;
    av.currentAppearance = key;
    const mats = this.matByTeam[team][variant];
    for (let i = 0; i < av.meshes.length; i++) {
      const idxs = av.meshTplIdx[i];
      av.meshes[i].material = idxs.length === 1 ? mats[idxs[0]] : idxs.map((ti) => mats[ti]);
    }
  }

  private tint(m: THREE.MeshStandardMaterial, colorHex: number, emissiveHex: number, translucent = false): THREE.Material {
    m.color = new THREE.Color(colorHex);
    m.emissive = new THREE.Color(emissiveHex);
    m.emissiveIntensity = 1.0;
    if (translucent) {
      m.transparent = true;
      m.opacity = 0.82;
      m.depthWrite = false;
    }
    return m;
  }

  /** Compute the effective scale factor for a given ant, including caste bump
   *  (soldiers read larger) and role override (player ant is bigger for visibility). */
  private scaleFor(a: AntState): number {
    if (a.casteType === 'queen') return 2.8;
    if (a.role === 'player') return 1.6;
    if (a.casteType === 'soldier') return 1.15;
    return 1.0;
  }

  /** Corpse pose: tipped on its side, sinking linearly into the terrain. */
  private writeDeadPose(root: THREE.Group, a: AntState): void {
    const sink = Math.min(0.04, a.deathT * 0.0015);
    const y = this.terrain.getHeight(a.x, a.z) + 0.002 - sink;
    // Flatten flat on the ground with a slight tilt — use the ant's heading
    // for the forward axis but rotate around it so the ant lays on its side.
    const fx = Math.cos(a.heading);
    const fz = Math.sin(a.heading);
    // Side axis (world +Z relative to heading).
    const sx = -fz;
    const sz = fx;
    // Body frame: forward unchanged, "up" = side axis (so body rolls 90°).
    const s = this.scaleFor(a);
    const e = root.matrix.elements;
    e[0] = fx * s;  e[1] = 0;       e[2] = fz * s;  e[3] = 0;
    e[4] = sx * s;  e[5] = 0.15 * s; e[6] = sz * s;  e[7] = 0;
    e[8] = 0;       e[9] = -1 * s;  e[10] = 0;      e[11] = 0;
    e[12] = a.x;    e[13] = y;      e[14] = a.z;    e[15] = 1;
    root.matrixWorldNeedsUpdate = true;
  }

  private writePose(root: THREE.Group, a: AntState): void {
    // Nest dive: when in a nestCycle, drop the ant below surface so it
    // visually vanishes into the black hole, then rises back up.
    let yBase = 0.002;
    if (a.nestCycleT > 0 && a.nestCycleDur > 0) {
      // Permanent dive (nurses, queen, recall) — nestCycleT >= 1e6 means
      // "stay underground indefinitely". Short-circuit the up/down curve so
      // these ants don't materialise at the surface when counters are equal.
      if (a.nestCycleT >= 1e6) {
        yBase = -1.2;
      } else {
        const p = 1 - a.nestCycleT / a.nestCycleDur;
        const dive = -1.2;
        if (p < 0.18) yBase = dive * (p / 0.18);
        else if (p > 0.82) yBase = dive * (1 - (p - 0.82) / 0.18);
        else yBase = dive;
      }
    }
    const y = this.terrain.getHeight(a.x, a.z) + yBase;
    this.terrain.getNormal(a.x, a.z, this.up);
    this.forward.set(Math.cos(a.heading), 0, Math.sin(a.heading));
    this.forward.addScaledVector(this.up, -this.forward.dot(this.up));
    if (this.forward.lengthSq() < 1e-6) {
      this.forward.set(1, 0, 0);
      this.forward.addScaledVector(this.up, -this.forward.dot(this.up));
    }
    this.forward.normalize();

    const bx = this.forward.y * this.up.z - this.forward.z * this.up.y;
    const by = this.forward.z * this.up.x - this.forward.x * this.up.z;
    const bz = this.forward.x * this.up.y - this.forward.y * this.up.x;

    const s = this.scaleFor(a);
    const e = root.matrix.elements;
    e[0] = this.forward.x * s; e[1] = this.forward.y * s; e[2] = this.forward.z * s; e[3] = 0;
    e[4] = this.up.x * s;      e[5] = this.up.y * s;      e[6] = this.up.z * s;      e[7] = 0;
    e[8] = bx * s;             e[9] = by * s;             e[10] = bz * s;            e[11] = 0;
    e[12] = a.x;               e[13] = y;                 e[14] = a.z;               e[15] = 1;
    root.matrixWorldNeedsUpdate = true;
  }
}
